import React from 'react';
import { Link } from 'react-router-dom';
import { useDashboardStore } from '../stores/dashboardStore';
import ProjectCarousel from '../components/ProjectCarousel';
import InternshipCardSimple from '../components/InternshipCardSimple';
import './Home.css';

const Home: React.FC = () => {
    const { config } = useDashboardStore();

    return (
        <div className="home">
            {/* Hero Section */}
            <section className="hero">
                <div className="hero-container">
                    <div className="hero-content">
                        <h1 className="hero-title">
                            Hello, I'm {config.personalInfo.name}
                        </h1>
                        <p className="hero-subtitle">{config.personalInfo.title}</p>
                        <p className="hero-description">{config.personalInfo.description}</p>
                        <div className="hero-actions">
                            <Link to="/resume" className="btn btn-primary">
                                Download Resume
                            </Link>
                            <a href="#projects" className="btn btn-secondary">
                                Browse Projects
                            </a>
                        </div>
                    </div>
                    <div className="hero-image">
                        <img src={config.personalInfo.avatar} alt={config.personalInfo.name} />
                    </div>
                </div>
            </section>

            {/* Education Section */}
            <section className="education-section">
                <div className="container">
                    <h2 className="section-title">{config.education.title}</h2>
                    <p className="section-description">{config.education.description}</p>

                    <div className="education-grid">
                        {config.education.experiences.map((exp, index) => (
                            <div key={index} className="education-card">
                                <div className="education-header">
                                    <h3>{exp.school}</h3>
                                    <span className="period">{exp.period}</span>
                                </div>
                                <div className="education-details">
                                    <p className="degree">{exp.degree} - {exp.major}</p>
                                    <div className="stats">
                                        {exp.gpa && <span className="stat-item">GPA: {exp.gpa}</span>}
                                        {exp.ranking && <span className="stat-item">Ranking: {exp.ranking}</span>}
                                    </div>
                                    {exp.honors && exp.honors.length > 0 && (
                                        <div className="honors">
                                            <strong>Major Honors:</strong>
                                            <ul>
                                                {exp.honors.map((honor, honorIndex) => (
                                                    <li key={honorIndex}>{honor}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {exp.papers && exp.papers.length > 0 && (
                                        <div className="papers">
                                            <strong>Academic Papers:</strong>
                                            <ul>
                                                {exp.papers.map((paper, paperIndex) => (
                                                    <li key={paperIndex}>{paper}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {exp.description && <p className="description">{exp.description}</p>}
                                    {exp.learningPoints && exp.learningPoints.length > 0 && (
                                        <div className="learning-points">
                                            <strong>Learning Points:</strong>
                                            <ul>
                                                {exp.learningPoints.map((point, pointIndex) => (
                                                    <li key={pointIndex}>{point}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {exp.relevance && (
                                        <div className="relevance">
                                            <strong>Relevant Courses:</strong>
                                            <p>{exp.relevance}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {config.education.courses && config.education.courses.length > 0 && (
                        <div className="courses-section">
                            <h3>Relevant Courses</h3>
                            <div className="courses-grid">
                                {config.education.courses.map((course, index) => (
                                    <div key={index} className="course-card">
                                        <h4>{course.name}</h4>
                                        <p>{course.description}</p>
                                        <div className="course-relevance">
                                            <strong>Relevance:</strong>
                                            <span>{course.relevance}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Projects Section */}
            <section id="projects" className="projects-section">
                <div className="container">
                    <h2 className="section-title">Personal Projects</h2>
                    <p className="section-description">
                        The following projects demonstrate my technical abilities and product thinking, each reflecting a high degree of alignment with the product manager role.
                    </p>
                    <ProjectCarousel />
                </div>
            </section>

            {/* Internships Section */}
            <section className="internships-section">
                <div className="container">
                    <h2 className="section-title">Internship Experience</h2>
                    <p className="section-description">
                        My internship experience at well-known companies has given me an in-depth understanding of the work content and core skills of product managers.
                    </p>
                    <div className="internships-grid-simple">
                        {config.internships.map((internship) => (
                            <InternshipCardSimple key={internship.id} internship={internship} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Book Reviews Section */}
            <section className="book-reviews-section">
                <div className="container">
                    <h2 className="section-title">Book Reviews</h2>
                    <p className="section-description">
                        Through reading relevant books, I continuously learn and improve my product management capabilities.
                    </p>
                    <div className="books-grid">
                        {config.bookReviews.map((book, index) => (
                            <div key={index} className="book-card">
                                <div className="book-cover">
                                    <img src={book.cover} alt={book.title} />
                                    <div className="book-rating">
                                        {'★'.repeat(book.rating)}{'☆'.repeat(5 - book.rating)}
                                    </div>
                                </div>
                                <div className="book-info">
                                    <h3>{book.title}</h3>
                                    <p className="author">Author: {book.author}</p>
                                    <p className="review">{book.review}</p>
                                    <div className="key-insights">
                                        <h4>Key Insights:</h4>
                                        <ul>
                                            {book.keyInsights.map((insight, insightIndex) => (
                                                <li key={insightIndex}>{insight}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
